import { Form, FormInstance } from "antd";
import { Ref, useState } from "react";
import {
  MdCheck,
  MdContentCopy,
  MdLink,
  MdRemove,
  MdShare,
} from "react-icons/md";
import toast from "react-hot-toast";
import { Subject } from "rxjs";
import { styled } from "styled-components";
import { colors } from "../../../theme/colors";
import { card } from "./section-card.component";
import { config } from "../../../config/config";
import {
  CUSTOM_NAME_LINK_ERROR,
  CUSTOM_NAME_MAX_LENGTH,
  getCustomNameValidationError,
  looksLikeUrlOrLink,
  sanitizeCustomName,
} from "../../../utils/functions/string/custom-url";

type Status = "idle" | "checking" | "available" | "taken";

const Shell = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
  width: 100%;
`;

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.text.hex};
  line-height: 1.2;

  svg {
    flex: 0 0 auto;
    color: ${colors.primary.hex};
    font-size: 1.5rem;
    opacity: 0.85;
  }
`;

const RequiredMark = styled.span`
  color: ${colors.error.hex};
  margin-left: 0.2rem;
`;

const Row = styled.div<{ $status: Status }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 4rem;
  background-color: ${card.innerBg};
  border: 1px solid
    ${({ $status }) =>
      $status === "available"
        ? "rgba(120, 190, 100, 0.55)"
        : $status === "taken"
          ? "rgba(239, 35, 60, 0.55)"
          : card.border};
  border-radius: 0.6rem;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: ${colors.primary.hex};
    box-shadow: 0 0 0 2px ${card.accentSoft};
  }
`;

const Prefix = styled.span`
  align-self: stretch;
  display: inline-flex;
  align-items: center;
  padding: 0 0.9rem;
  font-size: 1.2rem;
  font-weight: 500;
  color: ${colors.text.hex};
  opacity: 0.55;
  background-color: ${card.bg};
  border-right: 1px solid ${card.border};
  white-space: nowrap;
  user-select: none;

  @media (max-width: 30rem) {
    font-size: 1.1rem;
    padding: 0 0.7rem;
  }
`;

const Slug = styled.input`
  flex: 1;
  min-width: 0;
  width: 100% !important;
  border: none;
  outline: none;
  background: transparent;
  padding: 0 0.9rem;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${colors.primary.hex};

  &::placeholder {
    color: ${colors.text.hex};
    opacity: 0.35;
    font-weight: 500;
  }
`;

const StatusDot = styled.div<{ $status: Status }>`
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  margin: 0 0.4rem 0 0.8rem;
  border-radius: 50%;
  transition: color 0.2s, background-color 0.2s;

  ${({ $status }) => {
    if ($status === "available")
      return `color: #2f7a3f; background-color: rgba(120, 190, 100, 0.16);`;
    if ($status === "taken")
      return `color: #b71c2b; background-color: rgba(239, 35, 60, 0.12);`;
    if ($status === "checking")
      return `color: ${colors.primary.hex}; background-color: rgba(32, 84, 150, 0.1);`;
    return `display: none;`;
  }}
`;

const Actions = styled.div`
  flex: 0 0 auto;
  align-self: stretch;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0 0.4rem;
  border-left: 1px solid ${card.border};
`;

const IconButton = styled.button<{ $flash?: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.hex};
  opacity: 0.7;
  transition: background-color 0.15s, color 0.15s, opacity 0.15s,
    transform 0.1s ease-out;

  &:hover {
    background-color: rgba(32, 84, 150, 0.1);
    color: ${colors.primary.hex};
    opacity: 1;
  }

  &:active {
    transform: scale(0.9);
    background-color: rgba(32, 84, 150, 0.2);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  ${({ $flash }) =>
    $flash
      ? `
        background-color: rgba(120, 190, 100, 0.22) !important;
        color: #2f7a3f !important;
        opacity: 1 !important;
      `
      : ""}
`;

const Spinner = styled.span`
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  animation: cu-spin 0.7s linear infinite;

  @keyframes cu-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Helper = styled.div`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.55;
  line-height: 1.4;
`;

const FieldError = styled.div`
  font-size: 1.15rem;
  color: ${colors.error.hex};
  line-height: 1.4;
`;

const statusLabel = (status: Status) => {
  if (status === "available") return "Available";
  if (status === "taken") return "Taken";
  if (status === "checking") return "Checking";
  return "";
};

const statusIcon = (status: Status) => {
  if (status === "available") return <MdCheck size={13} />;
  if (status === "taken") return <MdRemove size={13} />;
  if (status === "checking") return <Spinner />;
  return null;
};

export const CustomUrlField = ({
  inputRef,
  subject$,
  form,
  customUrl,
  urlVerified,
  showVerification,
  placeholder = "your-film-name",
}: {
  inputRef: Ref<HTMLInputElement>;
  subject$: Subject<string>;
  form: FormInstance;
  customUrl: string;
  urlVerified: boolean | null;
  showVerification: boolean;
  placeholder?: string;
}) => {
  const [copiedFlash, setCopiedFlash] = useState(false);
  const [sharedFlash, setSharedFlash] = useState(false);

  const trimmed = (customUrl ?? "").trim();
  const status: Status = !trimmed
    ? "idle"
    : !showVerification
      ? "checking"
      : urlVerified === null
        ? "checking"
        : urlVerified
          ? "available"
          : "taken";

  const publicHost = config.public_site_host;
  const fullUrl = `https://${publicHost}/${trimmed}`;
  const canShare = !!trimmed && urlVerified !== false;

  const flash = (setter: (v: boolean) => void) => {
    setter(true);
    window.setTimeout(() => setter(false), 600);
  };

  const handleCopy = () => {
    if (!trimmed) {
      toast.error("Enter a custom URL first");
      return;
    }
    window.navigator.clipboard.writeText(fullUrl);
    toast.success("Link copied!");
    flash(setCopiedFlash);
  };

  const handleShare = () => {
    if (!trimmed) {
      toast.error("Enter a custom URL first");
      return;
    }
    flash(setSharedFlash);
    if (
      (window.navigator as any).share &&
      window.navigator.canShare?.({ url: fullUrl })
    ) {
      try {
        navigator.share({ url: fullUrl });
        return;
      } catch (_) {}
    }
    window.navigator.clipboard.writeText(fullUrl);
    toast.success("Link copied!");
  };

  const sLabel = statusLabel(status);

  const applyCustomName = (raw: string) => {
    const el =
      typeof inputRef === "object" && inputRef?.current
        ? inputRef.current
        : null;

    if (looksLikeUrlOrLink(raw)) {
      if (el) el.value = "";
      form.setFieldValue("custom_name", "");
      subject$.next("");
      form.setFields([
        { name: "custom_name", errors: [CUSTOM_NAME_LINK_ERROR] },
      ]);
      return;
    }

    const cleaned = sanitizeCustomName(raw, CUSTOM_NAME_MAX_LENGTH);
    if (el) el.value = cleaned;
    form.setFieldValue("custom_name", cleaned);
    subject$.next(cleaned);

    const error = getCustomNameValidationError(cleaned);
    form.setFields([
      { name: "custom_name", errors: error ? [error] : [] },
    ]);
  };

  return (
    <Shell>
      <Label>
        <MdLink />
        <span>
          Custom URL
          <RequiredMark>*</RequiredMark>
        </span>
      </Label>
      <Row $status={status}>
        <Prefix>{publicHost}/</Prefix>
        <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
          <Form.Item
            name="custom_name"
            required
            rules={[
              { required: true, message: "Required!" },
              {
                validator: async (_, value) => {
                  const error = getCustomNameValidationError(value);
                  if (error) {
                    return Promise.reject(new Error(error));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            noStyle
          >
            <Slug
              autoComplete="off"
              name="custom_name"
              placeholder={placeholder}
              maxLength={CUSTOM_NAME_MAX_LENGTH}
              ref={inputRef}
              onInput={(e) => {
                applyCustomName((e.target as HTMLInputElement).value);
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData("text") ?? "";
                if (looksLikeUrlOrLink(pasted)) {
                  e.preventDefault();
                  applyCustomName(pasted);
                }
              }}
            />
          </Form.Item>
        </div>
        {sLabel ? (
          <StatusDot $status={status} title={sLabel} aria-label={sLabel}>
            {statusIcon(status)}
          </StatusDot>
        ) : null}
        <Actions>
          <IconButton
            type="button"
            onClick={handleCopy}
            disabled={!canShare}
            aria-label="Copy link"
            title="Copy link"
            $flash={copiedFlash}
          >
            {copiedFlash ? <MdCheck size={16} /> : <MdContentCopy size={15} />}
          </IconButton>
          <IconButton
            type="button"
            onClick={handleShare}
            disabled={!canShare}
            aria-label="Share link"
            title="Share link"
            $flash={sharedFlash}
          >
            {sharedFlash ? <MdCheck size={16} /> : <MdShare size={15} />}
          </IconButton>
        </Actions>
      </Row>
      <Form.Item noStyle shouldUpdate>
        {() => {
          const errors = form.getFieldError("custom_name");
          if (errors.length > 0) {
            return <FieldError>{errors[0]}</FieldError>;
          }
          return (
            <Helper>
              Short name for your public page — lowercase letters, numbers, and
              hyphens only. Do not paste Google Drive, YouTube, or other links.
            </Helper>
          );
        }}
      </Form.Item>
    </Shell>
  );
};
